/**
 * Providers Component
 * 
 * A wrapper component that provides essential context providers to the application.
 * Sets up React Query for data fetching and toast notifications. Theme management
 * lives solely in context/theme-provider.tsx to avoid nested next-themes providers.
 * 
 * Features:
 * - React Query client setup
 * - Toast notification system
 * - Client-side rendering
 */

'use client';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from '@/components/ui/toaster';

/**
 * React Query client instance with default configuration
 * @constant
 * @type {QueryClient}
 */
const queryClient = new QueryClient();

/**
 * Providers Component
 * 
 * @component
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to be wrapped
 * @returns {JSX.Element} The providers component
 * 
 * @example
 * <Providers>
 *   <App />
 * </Providers>
 */
export function Providers({ children }: { children: React.ReactNode }): JSX.Element {
	return (
		<QueryClientProvider client={queryClient}>
			{children}
			<Toaster />
		</QueryClientProvider>
	);
}
